/* Generated from cuelume. Run npm run audio to update. */
/**
 * The audio engine — synthesizes each sound live via the Web Audio API
 * on one shared, lazily created `AudioContext`. No audio files, no
 * dependencies. Every sound carries a gentle envelope (and often a soft
 * shimmer tail) instead of a hard transient, so nothing feels harsh.
 */

const RECIPES = {
  chime: {
    masterGain: 0.5,
    layers: [
      { kind: 'tone', waveform: 'sine', frequency: 1046.5, attack: 0.006, decay: 0.22, peak: 0.09 },
      { kind: 'tone', waveform: 'sine', frequency: 1568, offset: 0.09, attack: 0.006, decay: 0.26, peak: 0.08 },
    ],
    shimmer: { delay: 0.12, feedback: 0.25, wet: 0.18, lowpass: 4000 },
  },
  error: {
    masterGain: 1,
    layers: [
      { kind: 'noise', filterType: 'bandpass', filterFrequency: 850, filterQ: 1.1, attack: 0.001, decay: 0.035, peak: 0.13 },
      { kind: 'tone', waveform: 'triangle', frequency: 440, offset: 0.025, attack: 0.004, decay: 0.09, peak: 0.045 },
      { kind: 'tone', waveform: 'triangle', frequency: 349.23, offset: 0.1, attack: 0.004, decay: 0.14, peak: 0.04 },
    ],
  },
  toggle: {
    masterGain: 1,
    layers: [
      { kind: 'noise', filterType: 'bandpass', filterFrequency: 2200, filterQ: 1.6, attack: 0.001, decay: 0.016, peak: 0.12 },
      { kind: 'noise', filterType: 'bandpass', filterFrequency: 3800, filterQ: 1.6, offset: 0.024, attack: 0.001, decay: 0.02, peak: 0.1 },
    ],
  },
};

function isSoundName(value) {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(RECIPES, value);
}

const SOURCE_STOP_PADDING = 0.05;
const CLEANUP_MARGIN = 0.05;
const INAUDIBLE_GAIN = 0.001;
function renderTone(context, destination, layer, startTime) {
    const oscillator = context.createOscillator();
    oscillator.type = layer.waveform;
    oscillator.frequency.setValueAtTime(layer.frequency, startTime);
    if (layer.detune)
        oscillator.detune.value = layer.detune;
    if (layer.glideTo !== undefined) {
        const glideTime = layer.glideTime ?? layer.attack + layer.decay;
        oscillator.frequency.exponentialRampToValueAtTime(layer.glideTo, startTime + glideTime);
    }
    const gain = context.createGain();
    gain.gain.setValueAtTime(0.0001, startTime);
    gain.gain.exponentialRampToValueAtTime(layer.peak, startTime + layer.attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, startTime + layer.attack + layer.decay);
    oscillator.connect(gain).connect(destination);
    oscillator.start(startTime);
    oscillator.stop(startTime + layer.attack + layer.decay + SOURCE_STOP_PADDING);
}
function renderNoise(context, destination, layer, startTime) {
    const duration = layer.attack + layer.decay + SOURCE_STOP_PADDING;
    const length = Math.max(1, Math.floor(duration * context.sampleRate));
    const buffer = context.createBuffer(1, length, context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i++)
        data[i] = 2 * Math.random() - 1;
    const source = context.createBufferSource();
    source.buffer = buffer;
    const filter = context.createBiquadFilter();
    filter.type = layer.filterType;
    filter.frequency.value = layer.filterFrequency;
    if (layer.filterQ !== undefined)
        filter.Q.value = layer.filterQ;
    const gain = context.createGain();
    gain.gain.setValueAtTime(0.0001, startTime);
    gain.gain.exponentialRampToValueAtTime(layer.peak, startTime + layer.attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, startTime + layer.attack + layer.decay);
    source.connect(filter).connect(gain).connect(destination);
    source.start(startTime);
    source.stop(startTime + duration);
}
/** Wires a soft echo/shimmer send off `source`, feeding back into `destination`. */
function attachShimmer(context, source, destination, shimmer) {
    const delay = context.createDelay(1);
    delay.delayTime.value = shimmer.delay;
    const feedbackFilter = context.createBiquadFilter();
    feedbackFilter.type = "lowpass";
    feedbackFilter.frequency.value = shimmer.lowpass;
    const feedbackGain = context.createGain();
    feedbackGain.gain.value = shimmer.feedback;
    const wetGain = context.createGain();
    wetGain.gain.value = shimmer.wet;
    source.connect(delay);
    delay.connect(feedbackFilter);
    feedbackFilter.connect(feedbackGain);
    feedbackGain.connect(delay);
    feedbackFilter.connect(wetGain);
    wetGain.connect(destination);
    return [delay, feedbackFilter, feedbackGain, wetGain];
}
function sourceEnd(recipe) {
    return Math.max(...recipe.layers.map((layer) => (layer.offset ?? 0) + layer.attack + layer.decay + SOURCE_STOP_PADDING));
}
function shimmerTail(shimmer) {
    if (!shimmer || shimmer.feedback <= 0)
        return 0;
    if (shimmer.feedback >= 1)
        return shimmer.delay;
    return shimmer.delay * (1 + Math.ceil(Math.log(INAUDIBLE_GAIN) / Math.log(shimmer.feedback)));
}
function renderRecipe(context, recipe) {
    const now = context.currentTime;
    const master = context.createGain();
    master.gain.value = recipe.masterGain;
    master.connect(context.destination);
    const shimmerNodes = recipe.shimmer
        ? attachShimmer(context, master, context.destination, recipe.shimmer)
        : [];
    for (const layer of recipe.layers) {
        const startTime = now + (layer.offset ?? 0);
        if (layer.kind === "tone")
            renderTone(context, master, layer, startTime);
        else
            renderNoise(context, master, layer, startTime);
    }
    const cleanupAfterMs = (sourceEnd(recipe) + shimmerTail(recipe.shimmer) + CLEANUP_MARGIN) * 1000;
    setTimeout(() => {
        master.disconnect();
        for (const node of shimmerNodes)
            node.disconnect();
    }, cleanupAfterMs);
}
let sharedContext = null;
let enabled = true;
/** Enables or disables future playback. Preference storage stays with the app. */
function setEnabled(value) {
    if (typeof value === "boolean")
        enabled = value;
}
function getAudioContext() {
    if (sharedContext)
        return sharedContext;
    if (typeof window === "undefined")
        return null;
    const Ctor = window.AudioContext ??
        window.webkitAudioContext;
    if (!Ctor)
        return null;
    try {
        sharedContext = new Ctor();
    }
    catch {
        return null;
    }
    return sharedContext;
}
/**
 * Plays a sound immediately. Safe to call from anywhere — lazily creates
 * the shared `AudioContext` on first use, resumes it if the browser
 * started it suspended (e.g. before any user gesture), and is a no-op
 * when Web Audio is unavailable (SSR, old browsers).
 */
function play(sound = "chime") {
    if (!enabled || !isSoundName(sound))
        return;
    const context = getAudioContext();
    if (!context)
        return;
    const recipe = RECIPES[sound];
    if (context.state === "running") {
        renderRecipe(context, recipe);
    }
    else {
        renderRecipe(context, recipe);
        try {
            void context.resume().catch(() => { });
        }
        catch {
            // Some browsers throw synchronously when audio is blocked.
        }
    }
}


window.SigmaThemeAudio = Object.freeze({
  playFavouriteTap() {
    try {
      play('chime');
    } catch (error) {}
  },
  playError() {
    try {
      play('error');
    } catch (error) {}
  },
  playThemeChange() {
    try {
      play('toggle');
    } catch (error) {}
  },
});
